<script setup lang="ts">
  /**
   * Portal Layout
   *
   * Layout for client-facing portal (portal.claimshieldpro.com).
   * Features:
   * - Premium dark purple/gold theme
   * - Collapsible sidebar navigation
   * - Header with user menu
   * - Responsive design
   */
  import { computed, onMounted, onUnmounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useAuthStore } from '../../stores/auth'
  import { useUIStore } from '../../stores/ui'

  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const uiStore = useUIStore()

  // Apply portal dark theme on mount
  onMounted(() => {
    document.body.classList.add('portal-dark')
  })

  onUnmounted(() => {
    document.body.classList.remove('portal-dark')
  })

  // Navigation items for portal
  const navItems = [
    { label: 'Dashboard', icon: 'pi pi-home', route: '/' },
    { label: 'Safety Scores', icon: 'pi pi-chart-line', route: '/safety-scores' },
    { label: 'Drivers', icon: 'pi pi-users', route: '/drivers' },
    { label: 'Vehicles', icon: 'pi pi-truck', route: '/vehicles' },
    { label: 'Training', icon: 'pi pi-book', route: '/training' },
    { label: 'Billing', icon: 'pi pi-credit-card', route: '/billing' },
    { label: 'Documents', icon: 'pi pi-folder', route: '/documents' },
    { label: 'Contacts', icon: 'pi pi-address-book', route: '/contacts' },
  ]

  // User menu items
  const userMenuItems = [
    { label: 'Settings', icon: 'pi pi-cog', command: () => router.push('/settings') },
    { separator: true },
    { label: 'Logout', icon: 'pi pi-sign-out', command: () => handleLogout() },
  ]

  // Theme controls
  const themeIcon = computed(() => {
    switch (uiStore.theme) {
      case 'dark':
        return 'pi pi-moon'
      case 'light':
        return 'pi pi-sun'
      default:
        return 'pi pi-desktop'
    }
  })

  const themeTooltip = computed(() => {
    switch (uiStore.theme) {
      case 'dark':
        return 'Dark mode (click for light)'
      case 'light':
        return 'Light mode (click for system)'
      default:
        return 'System theme (click for dark)'
    }
  })

  function cycleTheme() {
    const order: ('light' | 'dark' | 'system')[] = ['light', 'dark', 'system']
    const currentIndex = order.indexOf(uiStore.theme)
    const nextIndex = (currentIndex + 1) % order.length
    uiStore.setTheme(order[nextIndex]!)
  }

  const isNavActive = (itemRoute: string) => {
    if (itemRoute === '/') return route.path === '/'
    return route.path.startsWith(itemRoute)
  }

  async function handleLogout() {
    await authStore.logout()
    router.push('/login')
  }
</script>

<template>
  <div class="flex h-screen portal-dark-bg">
    <!-- Sidebar -->
    <aside
      :class="[
        'portal-sidebar fixed inset-y-0 left-0 z-30 flex flex-col transition-all duration-300',
        uiStore.sidebarCollapsed ? 'w-16' : 'w-64',
        uiStore.sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ]"
    >
      <!-- Logo area -->
      <div class="portal-sidebar-header flex h-16 items-center gap-3 border-b px-4">
        <!-- Shield icon -->
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary-700 to-accent-500">
          <svg class="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          </svg>
        </div>
        <template v-if="!uiStore.sidebarCollapsed">
          <div class="min-w-0">
            <span class="block truncate text-lg font-bold text-white">
              {{ authStore.clientName || 'ClaimShield' }}
            </span>
            <span class="text-xs text-accent-500">Portal</span>
          </div>
        </template>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 space-y-1 overflow-y-auto p-3">
        <RouterLink
          v-for="item in navItems"
          :key="item.route"
          :to="item.route"
          :class="[
            'sidebar-item',
            isNavActive(item.route) ? 'sidebar-item-active' : '',
            uiStore.sidebarCollapsed ? 'justify-center px-0' : '',
          ]"
          v-tooltip.right="uiStore.sidebarCollapsed ? item.label : undefined"
        >
          <i :class="[item.icon, 'text-lg']" />
          <span v-if="!uiStore.sidebarCollapsed">{{ item.label }}</span>
        </RouterLink>
      </nav>

      <!-- Collapse toggle -->
      <button
        class="collapse-toggle hidden lg:flex h-12 items-center justify-center border-t"
        @click="uiStore.toggleSidebar"
      >
        <i :class="uiStore.sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'" />
      </button>
    </aside>

    <!-- Mobile sidebar overlay -->
    <div
      v-if="uiStore.sidebarMobileOpen"
      class="fixed inset-0 z-20 bg-black/70 backdrop-blur-sm lg:hidden"
      @click="uiStore.closeMobileSidebar"
    />

    <!-- Main content area -->
    <div
      :class="[
        'flex flex-1 flex-col transition-all duration-300',
        uiStore.sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
      ]"
    >
      <!-- Header -->
      <header class="portal-header sticky top-0 z-10 flex h-16 items-center justify-between border-b px-4 lg:px-6">
        <!-- Mobile menu button -->
        <button
          class="rounded-lg p-2 text-secondary-400 hover:bg-primary-900/30 hover:text-white lg:hidden"
          @click="uiStore.toggleMobileSidebar"
        >
          <i class="pi pi-bars text-xl" />
        </button>

        <!-- Page title (optional) -->
        <div class="hidden lg:block">
          <h1 class="text-lg font-semibold text-white">
            {{ route.meta.title?.toString().split(' | ')[0] || 'Dashboard' }}
          </h1>
        </div>

        <!-- Right side: theme + notifications + user menu -->
        <div class="flex items-center gap-3">
          <!-- Theme Toggle -->
          <button
            class="portal-header-btn"
            @click="cycleTheme"
            v-tooltip.bottom="themeTooltip"
          >
            <i :class="themeIcon" class="text-lg" />
          </button>

          <!-- User menu -->
          <PMenu ref="userMenu" :model="userMenuItems" popup />
          <button
            class="flex items-center gap-2 rounded-lg p-1 hover:bg-primary-900/30"
            @click="($refs.userMenu as any).toggle($event)"
          >
            <PAvatar
              :label="authStore.userInitials"
              class="portal-avatar"
              shape="circle"
            />
            <span class="hidden text-sm font-medium text-secondary-300 md:block">
              {{ authStore.userDisplayName }}
            </span>
            <i class="pi pi-chevron-down text-xs text-secondary-500" />
          </button>
        </div>
      </header>

      <!-- Page content -->
      <main class="flex-1 overflow-y-auto">
        <div class="page-container">
          <RouterView />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Portal dark background gradient */
.portal-dark-bg {
  background: linear-gradient(135deg, #0e0920 0%, #1c1240 50%, #0e0920 100%);
  background-attachment: fixed;
}

/* Sidebar styling */
.portal-sidebar {
  background: rgba(14, 9, 32, 0.95);
  border-right: 1px solid rgba(61, 38, 131, 0.3);
  backdrop-filter: blur(10px);
}

.portal-sidebar-header {
  border-color: rgba(61, 38, 131, 0.3);
}

/* Header styling */
.portal-header {
  background: rgba(14, 9, 32, 0.9);
  border-color: rgba(61, 38, 131, 0.3);
  backdrop-filter: blur(10px);
}

.portal-header-btn {
  @apply flex h-9 w-9 items-center justify-center rounded-lg;
  @apply text-secondary-400 transition-colors;
}

.portal-header-btn:hover {
  background: rgba(61, 38, 131, 0.4);
  @apply text-white;
}

/* Avatar with gradient */
.portal-avatar {
  background: linear-gradient(135deg, #3d2683 0%, #c9a962 100%) !important;
  color: white !important;
}

/* Collapse toggle */
.collapse-toggle {
  border-color: rgba(61, 38, 131, 0.3);
  @apply text-secondary-500;
}

.collapse-toggle:hover {
  background: rgba(61, 38, 131, 0.3);
  @apply text-white;
}
</style>
